/* =========================================================================
   ClueVine — shared scoring
   A mode reports a raw `base` score plus the line items that sum to it. This
   layer adds the cross-cutting pieces every mode shares: a speed bonus, the
   difficulty multiplier, the streak multiplier, and the hint penalty. The
   returned breakdown's line values always sum to its total, so the Result
   screen can render an honest, line-by-line receipt.
   ========================================================================= */

import type { ModeScore, ScoreBreakdown, ScoreContext, ScoreLine } from './types';

/** Maximum speed bonus when a puzzle is solved instantly. */
export const MAX_SPEED_BONUS = 300;

/** +5% per consecutive daily, capped at +50% (streak of 10+). */
export function streakMultiplier(streak: number): number {
  const clamped = Math.min(Math.max(streak, 0), 10);
  return 1 + clamped * 0.05;
}

/** A linearly-decaying bonus: full at 0ms, zero at/after par. */
export function speedBonus(elapsedMs: number, parMs: number): number {
  if (!parMs || parMs <= 0) return 0;
  const ratio = 1 - elapsedMs / parMs;
  return Math.max(0, Math.round(MAX_SPEED_BONUS * ratio));
}

export function computeScore(modeScore: ModeScore, ctx: ScoreContext): ScoreBreakdown {
  const lines: ScoreLine[] = modeScore.lines.map((line) => ({ ...line }));
  let running = Math.round(modeScore.base);

  const par = modeScore.parMs ?? 0;
  const bonus = par > 0 ? speedBonus(ctx.elapsedMs, par) : 0;
  if (bonus > 0) {
    lines.push({ label: 'Speed bonus', value: bonus, kind: 'bonus' });
    running += bonus;
  }

  const diffMult = ctx.difficulty.scoreMultiplier;
  if (diffMult !== 1 && running !== 0) {
    const after = Math.round(running * diffMult);
    const delta = after - running;
    if (delta !== 0) {
      lines.push({
        label: `Difficulty · ${ctx.difficulty.label}`,
        value: delta,
        kind: 'multiplier',
        display: `×${diffMult}`,
      });
    }
    running = after;
  }

  const sMult = streakMultiplier(ctx.streak);
  if (ctx.streak > 0 && sMult !== 1 && running !== 0) {
    const after = Math.round(running * sMult);
    const delta = after - running;
    if (delta !== 0) {
      lines.push({
        label: `Streak · ${ctx.streak} day${ctx.streak === 1 ? '' : 's'}`,
        value: delta,
        kind: 'multiplier',
        display: `×${sMult.toFixed(2)}`,
      });
    }
    running = after;
  }

  if (ctx.hintPenalty > 0) {
    lines.push({ label: `Hints · ${ctx.hintsUsed}`, value: -ctx.hintPenalty, kind: 'penalty' });
    running -= ctx.hintPenalty;
  }

  return { lines, total: Math.max(0, running) };
}
